/**
 * S2 — Campaign Approval detail (`/approvals/:campaignId`). The three-layer
 * confirm flow: two warranty checkboxes → primary "Approve campaign" → a
 * double-confirm modal. On confirm the record becomes an immutable quiet
 * "Approved DD.MM HH:MM" state (no edit/delete, no e-signature). A stale
 * snapshot or version conflict (409) never approves — it reloads + warns.
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S2.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Skeleton,
  toast,
} from '@teivor/shared-ui';
import { formatDate, formatDateTime } from '@teivor/shared-utils';
import { ConflictError } from '@teivor/shared-utils/api';

import { PageContainer } from '../../../components/PageContainer';
import type { CampaignContent } from '../types';
import { useApprovalDetailQuery, useConfirmApprovalMutation } from '../queries';

import { campaignTitle, reapprovalReasonLabel, statusBadge } from './approval-shared';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

export function ApprovalDetailPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  // Accept either route param name (`:campaignId` per spec, or the legacy `:id`).
  const params = useParams<{ campaignId?: string; id?: string }>();
  const campaignId = params.campaignId ?? params.id;

  const { data: approval, isLoading, isError, refetch } = useApprovalDetailQuery(campaignId);
  const confirmMutation = useConfirmApprovalMutation();

  const [warranty1, setWarranty1] = React.useState(false);
  const [warranty2, setWarranty2] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const bothChecked = warranty1 && warranty2;
  const isAwaiting = approval?.status === 'awaiting';

  const doConfirm = React.useCallback(() => {
    if (!approval) return;
    confirmMutation.mutate(
      {
        approvalId: approval.id,
        body: {
          warrantyCheckbox1: true,
          warrantyCheckbox2: true,
          doubleConfirmed: true,
          version: approval.version,
          expectedSnapshotRef: approval.campaignSnapshotRef,
        },
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setWarranty1(false);
          setWarranty2(false);
          toast.success(t('com.approvals.confirmed_toast', 'Kampaň schválena / Campaign approved'));
          void refetch();
        },
        onError: (err) => {
          setDialogOpen(false);
          if (err instanceof ConflictError) {
            toast.error(
              t(
                'com.approvals.stale_toast',
                'Kampaň se mezitím změnila — zkontrolujte prosím znovu / The campaign changed — please review again',
              ),
            );
            setWarranty1(false);
            setWarranty2(false);
            void refetch();
          } else {
            toast.error(t('com.approvals.confirm_error', 'Schválení se nezdařilo / Approval failed'));
          }
        },
      },
    );
  }, [approval, confirmMutation, refetch, t]);

  return (
    <PageContainer width="narrow">
      <button
        type="button"
        onClick={() => navigate('/approvals')}
        className="self-start text-[13px] leading-[18px] text-[var(--tvl-ink-2)] hover:text-[var(--tvl-ink)] outline-none focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2 rounded-[var(--tvl-r1)]"
      >
        ← {t('com.approvals.back', 'Zpět na schválení / Back to approvals')}
      </button>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : isError || !approval ? (
        <EmptyState
          variant="error-loading"
          title={t('com.approvals.detail_error', 'Schválení se nepodařilo načíst / Could not load this approval')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : (
        <>
          <Card>
            {isAwaiting && approval.reapprovalReason ? (
              <div className="flex items-start gap-2 rounded-t-[var(--tvl-r3)] bg-[var(--tvl-accent)] px-4 py-2 text-[13px] leading-[18px] text-[var(--tvl-on-accent)]">
                <span>
                  {t('com.approvals.reapproval_banner', 'Změna kampaně — vyžaduje nové schválení / Campaign changed — re-confirmation required')}
                  {` · ${reapprovalReasonLabel(approval.reapprovalReason, t)}`}
                </span>
              </div>
            ) : null}
            <CardHeader className="flex-row items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <CardTitle>{campaignTitle(approval, t)}</CardTitle>
                <span
                  className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)] tabular-nums"
                  style={{ fontFamily: 'var(--tvl-font-mono)' }}
                >
                  {approval.campaignSnapshotRef}
                </span>
              </div>
              <Badge variant={statusBadge(approval.status).variant}>
                {t(statusBadge(approval.status).key, statusBadge(approval.status).fallback)}
              </Badge>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {/* What the client is approving — resolved server-side from out.* (honest "—" per field). */}
              <CampaignContentBlock content={approval.campaignContent ?? null} locale={locale} />

              {approval.status === 'confirmed' ? (
                <div className="flex flex-col gap-1 rounded-[var(--tvl-r2)] bg-[var(--tvl-sunken)] p-3">
                  <p className="text-[14px] font-medium leading-[20px] text-[var(--tvl-ink)] tabular-nums">
                    {t('com.approvals.approved_at', 'Schváleno / Approved')}{' '}
                    {approval.confirmedAt ? formatDateTime(approval.confirmedAt, locale) : ''}
                  </p>
                  {approval.confirmedByName || approval.confirmedByUserId ? (
                    <p className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">
                      {t('com.approvals.recorded_user', 'Zaznamenaný uživatel / Recorded user')}:{' '}
                      {approval.confirmedByName ? (
                        <>
                          <span className="text-[var(--tvl-ink-2)]">{approval.confirmedByName}</span>
                          {approval.confirmedByUserId ? (
                            <span
                              className="ml-2 text-[11px] text-[var(--tvl-ink-3)]"
                              style={{ fontFamily: 'var(--tvl-font-mono)' }}
                            >
                              {approval.confirmedByUserId.slice(0, 8)}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span style={{ fontFamily: 'var(--tvl-font-mono)' }}>{approval.confirmedByUserId}</span>
                      )}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {approval.status === 'superseded_by_reapproval' ? (
                <InlineAlert variant="warning">
                  {t('com.approvals.superseded_notice', 'Toto schválení bylo nahrazeno novým. / This approval was superseded by a newer one.')}
                </InlineAlert>
              ) : null}
            </CardContent>
          </Card>

          {/* Confirm flow — awaiting only */}
          {isAwaiting ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('com.approvals.confirm_title', 'Potvrzení / Confirmation')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Checkbox isSelected={warranty1} onChange={setWarranty1}>
                  {t(
                    'com.approvals.warranty1',
                    'Potvrzuji, že máme oprávnění oslovit toto publikum. / I confirm we are authorized to contact this audience.',
                  )}
                </Checkbox>
                <Checkbox isSelected={warranty2} onChange={setWarranty2}>
                  {t(
                    'com.approvals.warranty2',
                    'Potvrzuji, že obsah a nastavení kampaně odpovídají naší dohodě. / I confirm the campaign content and settings match our agreement.',
                  )}
                </Checkbox>
                <div>
                  <Button
                    variant="primary"
                    disabled={!bothChecked}
                    onClick={() => setDialogOpen(true)}
                  >
                    {t('com.approvals.approve', 'Schválit kampaň / Approve campaign')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <ConfirmDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            title={t('com.approvals.double_confirm_title', 'Opravdu schválit? / Confirm approval?')}
            description={t(
              'com.approvals.double_confirm_desc',
              'Schválení je nevratné a zaznamenává se. / Approval is final and is recorded.',
            )}
            confirmLabel={t('com.approvals.double_confirm_yes', 'Ano, schválit / Yes, approve')}
            cancelLabel={t('common.cancel', 'Zrušit / Cancel')}
            onConfirm={doConfirm}
            loading={confirmMutation.isPending}
          />
        </>
      )}
    </PageContainer>
  );
}

// ─── Campaign content summary ────────────────────────────────────────────────

const DASH = '—';

/** One label / value row of the content summary. Value falls back to "—" honestly. */
function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="shrink-0 text-[12px] leading-[18px] text-[var(--tvl-ink-3)]">{label}</span>
      <span className="min-w-0 text-right text-[13px] leading-[18px] text-[var(--tvl-ink)]">{value}</span>
    </div>
  );
}

/**
 * The resolved "what you are approving" summary (campaign name, run mode, ICP, sequence + step count,
 * audience size, end condition). `content === null` means the campaign row could not be resolved — an
 * honest info state, never fabricated fields. Each individual null field renders as "—".
 */
function CampaignContentBlock({
  content,
  locale,
}: {
  content: CampaignContent | null;
  locale: 'cs-CZ' | 'en-US';
}) {
  const { t } = useTranslation();

  if (!content) {
    return (
      <InlineAlert variant="info">
        {t(
          'com.approvals.content_unavailable',
          'Podrobnosti kampaně se nepodařilo načíst. Schvalujete nastavení dle reference výše. / The campaign details could not be loaded. You are confirming the configuration referenced above.',
        )}
      </InlineAlert>
    );
  }

  const runModeLabel =
    content.runMode === 'one_time'
      ? t('com.approvals.run_mode.one_time', 'Jednorázová / One-time')
      : content.runMode === 'evergreen'
        ? t('com.approvals.run_mode.evergreen', 'Průběžná / Evergreen')
        : DASH;

  const steps =
    content.sequenceName != null
      ? content.sequenceStepCount != null
        ? `${content.sequenceName} · ${t('com.approvals.step_count', '{{count}} kroků / {{count}} steps', { count: content.sequenceStepCount })}`
        : content.sequenceName
      : DASH;

  // End condition: an explicit end date, else a lead quota, else open-ended ("until stopped").
  const endCondition =
    content.scheduledEndAt != null
      ? formatDate(content.scheduledEndAt, locale)
      : content.leadQuota != null
        ? t('com.approvals.quota_leads', '{{count}} leadů / {{count}} leads', { count: content.leadQuota })
        : t('com.approvals.end_open', 'Bez koncového data / Open-ended');

  // Delays between the follow-up emails — the "prodlevy mezi e-maily" the client asked to see.
  const delays =
    content.stepGaps != null && content.stepGaps.length > 0
      ? `${content.stepGaps.join(' · ')} ${
          content.stepGapMode === 'all_days'
            ? t('com.approvals.gap_all_days', 'kalendářních dní / calendar days')
            : t('com.approvals.gap_sending_days', 'pracovních dní / working days')
        }`
      : DASH;

  const pace =
    content.peoplePerDay != null
      ? t('com.approvals.pace_people', '{{count}} nových leadů/den / new leads/day', {
          count: content.peoplePerDay,
        })
      : content.dailyVolume != null
        ? t('com.approvals.pace_emails', '{{count}} e-mailů/den / emails/day', { count: content.dailyVolume })
        : DASH;

  return (
    <div className="flex flex-col gap-2 rounded-[var(--tvl-r2)] bg-[var(--tvl-sunken)] p-3">
      <span className="text-[12px] font-medium leading-[16px] text-[var(--tvl-ink-2)]">
        {t('com.approvals.content_title', 'Co schvalujete / What you are approving')}
      </span>
      <div className="flex flex-col gap-1.5">
        <SummaryRow
          label={t('com.approvals.field.name', 'Kampaň / Campaign')}
          value={content.campaignName ?? DASH}
        />
        <SummaryRow label={t('com.approvals.field.run_mode', 'Režim / Run mode')} value={runModeLabel} />
        <SummaryRow
          label={t('com.approvals.field.icp', 'Cílení (ICP) / Targeting (ICP)')}
          value={content.icpName ?? DASH}
        />
        <SummaryRow label={t('com.approvals.field.sequence', 'Sekvence / Sequence')} value={steps} />
        <SummaryRow
          label={t('com.approvals.field.audience', 'Publikum / Audience')}
          value={
            content.audienceSize != null
              ? t('com.approvals.contacts', '{{count}} kontaktů / {{count}} contacts', {
                  count: content.audienceSize,
                })
              : DASH
          }
        />
        <SummaryRow
          label={t('com.approvals.field.delays', 'Rozestupy e-mailů / Email delays')}
          value={delays}
        />
        <SummaryRow label={t('com.approvals.field.pace', 'Tempo odesílání / Send pace')} value={pace} />
        <SummaryRow
          label={t('com.approvals.field.end', 'Ukončení / End condition')}
          value={endCondition}
        />
      </div>
    </div>
  );
}
